import { useState } from "react";
import EventLog from "./EventLog";
import SessionControls from "./SessionControls";
import {
  Box,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import DefaultLayout from "../../layouts/default";
import MessageList from "./MessageList";
import { useAssistantSession } from "./hooks/useAssistantSession";
import ChatContainer from "./ChatContainer";

// Icons
import { FaCopy, FaFilter } from "react-icons/fa6";
import { faRobot } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TbCaretUpDownFilled } from "react-icons/tb";
import { TbCaretDownFilled } from "react-icons/tb";
import { TbCaretUpFilled } from "react-icons/tb";

const ExpandIcon = () => <TbCaretUpDownFilled />;

const CollapseIcon = () => {
  return (
    <VStack alignItems="center" p={0} m={0} spacing={-1} gap={-1}>
      <TbCaretDownFilled style={{ marginBottom: -5.5 }} />
      <TbCaretUpFilled style={{ marginTop: -4 }} />
    </VStack>
  );
};

const Assistant = () => {
  const [isInChat, setIsInChat] = useState(true);
  const [filterByValues, setFilterByValues] = useState(["dialog", "commands"]);

  const {
    isSessionActive,
    events,
    sessionError,
    startSession,
    stopSession,
    sendTextMessage,
    messages,
    currentAssistantMessage,
    currentUserMessage,
    copyChat,
  } = useAssistantSession();

  return (
    <DefaultLayout withFooter={false}>
      <ChatContainer>
        <Box
          borderBottom="1px solid"
          borderColor="gray.200"
          bg="white"
          py={4}
          px={6}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading
            as="h2"
            size="md"
            fontWeight="600"
            color="blue.700"
            display="flex"
            alignItems="center"
            gap={3}
          >
            <Box as="span" color="blue.500">
              <FontAwesomeIcon icon={faRobot} />
            </Box>
            FQL Assistant
          </Heading>
          <Box>
            <Tooltip
              label="Copy to clipboard"
              placement="bottom"
              openDelay={0}
              closeDelay={100}
            >
              <IconButton
                variant={"ghost"}
                aria-label="Copy"
                color="rgb(0,0,0,0.5)"
                size="lg"
                icon={<FaCopy />}
                onClick={() =>
                  copyChat({
                    showDialog: filterByValues.includes("dialog"),
                    showCommands: filterByValues.includes("commands"),
                  })
                }
              />
            </Tooltip>
            <Menu closeOnSelect={false}>
              <Tooltip
                label="Filters"
                placement="bottom"
                openDelay={0}
                closeDelay={100}
              >
                <MenuButton
                  as={IconButton}
                  sx={{ color: "rgb(0,0,0,0.5)" }}
                  icon={<FaFilter />}
                  size="lg"
                  aria-label="Filters"
                  variant={"ghost"}
                ></MenuButton>
              </Tooltip>
              <MenuList minWidth="240px">
                <MenuOptionGroup
                  title="Filter by"
                  type="checkbox"
                  value={filterByValues}
                  onChange={(values) => setFilterByValues(values)}
                >
                  <MenuItemOption value="dialog" _checked>
                    Dialog
                  </MenuItemOption>
                  <MenuItemOption value="commands" _checked>
                    Commands
                  </MenuItemOption>
                </MenuOptionGroup>
              </MenuList>
            </Menu>

            {isInChat ? (
              <Tooltip
                label="Show Logs"
                placement="bottom"
                openDelay={0}
                closeDelay={100}
              >
                <IconButton
                  variant={"ghost"}
                  aria-label="Logs"
                  color="rgb(0,0,0,0.5)"
                  size="lg"
                  icon={<CollapseIcon />}
                  onClick={() => setIsInChat(false)}
                />
              </Tooltip>
            ) : (
              <Tooltip
                label="Show Messages"
                placement="bottom"
                openDelay={0}
                closeDelay={100}
              >
                <IconButton
                  variant={"ghost"}
                  aria-label="Messages"
                  color="rgb(0,0,0,0.5)"
                  size="lg"
                  icon={<ExpandIcon />}
                  onClick={() => setIsInChat(true)}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box flex={1} overflowY="auto" pb={4} px={4}>
          {isInChat ? (
            <MessageList
              messages={messages}
              currentAssistantMessage={currentAssistantMessage}
              currentUserMessage={currentUserMessage}
              showDialog={filterByValues.includes("dialog")}
              showCommands={filterByValues.includes("commands")}
            />
          ) : (
            <EventLog events={events} />
          )}
        </Box>

        <SessionControls
          startSession={startSession}
          stopSession={stopSession}
          sendTextMessage={sendTextMessage}
          isSessionActive={isSessionActive}
          sessionError={sessionError}
        />
      </ChatContainer>
    </DefaultLayout>
  );
};

export default Assistant;
